import { Container, StringHelper } from "@bryntum/schedulerpro";
import { showConfirmationDialog, showToast } from "../../../other/utils/utils.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { deleteCompanyCamPhoto, getCompanyCamPhotos } from "../../../network/network.jsx";
import { AnnotationEditor } from "@markerjs/markerjs-ui";
import { getBryntumFloatRootElement } from "@/other/utils/domUtils.jsx";

// Todo: if there is no data to display then show a message to user that there are no photos available in this project

const COMPANY_CAM_PHOTOS_PER_PAGE = 25;
const COLUMN_COUNT = 6;
const COLUMN_GAP = 10;
const SCROLL_BUFFER = 25;

export class SwivuCompanyCamGalleryView extends Container {
  static get $name() {
    return "SwivuCompanyCamGalleryView";
  }

  static get type() {
    return "swivuCompanyCamGalleryView";
  }

  static get configurable() {
    return {
      contentElementCls: "h-full",
      items: {
        galleryViewMessageBox: {
          html: "No data yet.",
        },
        projectPhotoGallery: {
          type: "container",
          contentElementCls: "company-cam-photo-gallery",
          flex: 1,
          height: "100%",
          items: Array.from({ length: COLUMN_COUNT }).map(() => ({
            type: 'container',
            cls: "cc-image-column flex-col items-start justify-start",
            flex: '1 1 0%',
            items: [],
            totalHeight: 0,
            style: `gap: ${COLUMN_GAP}px`
          })),
          scrollable: {
            overflowY: "auto",
            listeners: {
              scroll: "up.onProjectPhotoScroll"
            }
          }
        }
      }
    };
  }

  loadRecord(record, context, otherParams) {
    // Clean up previous resources before loading a new record
    this.cleanBlobUrls();
    this.context = context;
    this.otherParams = otherParams;

    // Reset pagination
    this.photosPageNumber = 1;
    this.loadMoreProjectPhotos = false;

    this.parentRecord = record;

    this.companyCamToken = this.context.project.extraBoardIntegrationsStore.find((item) => item.key === "companyCam");
    this.swivuIsHasCCProject = this.companyCamToken && this.parentRecord.companyCamProject;

    this.clearProjectPhotoGalleryItems();
    this.loadProject()
  }

  clearProjectPhotoGalleryItems() {
    const { projectPhotoGallery } = this.widgetMap;

    // If gallery doesn't exist or has no items, initialize it
    if (!projectPhotoGallery || !projectPhotoGallery.items || !projectPhotoGallery.items.length) {
      if (projectPhotoGallery) {
        // Re-initialize the gallery with empty columns
        projectPhotoGallery.items = Array.from({ length: COLUMN_COUNT }).map(() => ({
          type: 'container',
          cls: "cc-image-column flex-col items-start justify-start",
          flex: '1 1 0%',
          items: [],
          totalHeight: 0,
          style: `gap: ${COLUMN_GAP}px`
        }));
      }
      return;
    }

    // Clear existing items
    projectPhotoGallery.items.forEach(column => {
      column.items = [];
      column.totalHeight = 0; // Reset column height to fix issues in image appending when project is changed
    });
  }

  getBoardFileStorePhotos() {
    const boardFileRecords = this.context.project.boardFilesStore.query((record) => record.fileType === "image" && record.taskId === this.parentRecord.id);

    return boardFileRecords.map((record) => ({
      id: record.id,
      isSwivuImage: true,
      uris: [
        {
          "type": "original_annotation",
          "uri": record.url,
          "url": record.url,
        },
        {
          "type": "thumbnail_annotation",
          "uri": record.thumbnailUrl,
          "url": record.thumbnailUrl
        }
      ]
    }))
  }

  async loadProject() {
    // Show loading mask
    this.toggleMask(true); // Force show mask
    this.isProjectPhotosLoading = this.isProjectDocsLoading = true;

    const galleryViewMessageBox = this.widgetMap.galleryViewMessageBox;

    try {
      // Load both CompanyCam photos and user uploaded images

      let projectPhotos = [];

      if (this.parentRecord.companyCamProject) {
        const [{ data }] = await Promise.all([
          this.getProjectPhotos(),
        ]);
        projectPhotos = data || [];
      }

      this.loadMoreProjectPhotos = projectPhotos.length === COMPANY_CAM_PHOTOS_PER_PAGE;

      const boardFilePhotos = this.getBoardFileStorePhotos();
      const allPhotos = [...boardFilePhotos, ...projectPhotos];
      this.swivuTotalPhotoCount = allPhotos.length;
      if (allPhotos.length) {
        galleryViewMessageBox.hide();
        await this.loadProjectPhotos(allPhotos);
      } else {
        galleryViewMessageBox.show();
      }
    } catch (e) {
      console.error('Error loading project:', e);
      // Show error toast or notification if needed
    } finally {
      this.isProjectPhotosLoading = this.isProjectDocsLoading = false;
      this.toggleMask(false); // Force hide mask
    }
  }

  getProjectPhotos() {
    if (!this.photosPageNumber) this.photosPageNumber = 1;
    return getCompanyCamPhotos(this.parentRecord.companyCamProject, this.companyCamToken?.value, { page: this.photosPageNumber++, per_page: COMPANY_CAM_PHOTOS_PER_PAGE })
  }

  async onProjectPhotoScroll({ srcElement }) {
    const { scrollTop, clientHeight, scrollHeight } = srcElement;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_BUFFER && !this.isProjectPhotosLoading && this.loadMoreProjectPhotos) {
      try {
        this.toggleMask(true); // Force show mask
        this.isProjectPhotosLoading = true;
        const { data } = await this.getProjectPhotos();
        await this.loadProjectPhotos(data);
        this.loadMoreProjectPhotos = data.length === COMPANY_CAM_PHOTOS_PER_PAGE;
      } catch (e) {
        showToast({ msg: this.parseCompanyCamErrorMessage(e) })
        console.error('Error loading more photos:', e);
      } finally {
        this.toggleMask(false); // Force hide mask
        this.isProjectPhotosLoading = false;
      }
    }
  }

  async getImageAspectsAndBlobUrl(imageURL, photo, isSwivuImage = false) {
    try {
      const imageResponse = await fetch(imageURL, isSwivuImage ? { credentials: "include" } : undefined);
      const imageBlob = await imageResponse.blob();
      const imageBitMap = await createImageBitmap(imageBlob);
      const imageObjectUrl = URL.createObjectURL(imageBlob);
      if (!this.imageBlobUrls) this.imageBlobUrls = [];
      this.imageBlobUrls.push(imageObjectUrl);
      return { url: imageObjectUrl, imageBitMap, photo }
    } catch (e) {
      return null;
    }
  }

  async loadProjectPhotos(photos) {
    if (!photos || !photos.length) return; // Exit if no photos to load

    const { projectPhotoGallery } = this.widgetMap;

    // Ensure the gallery is properly initialized
    if (!projectPhotoGallery || !projectPhotoGallery.items || !projectPhotoGallery.items.length) {
      this.clearProjectPhotoGalleryItems();
      // After initialization, check again if gallery is ready
      if (!projectPhotoGallery || !projectPhotoGallery.items || !projectPhotoGallery.items.length) {
        console.error('Failed to initialize gallery');
        return; // Exit if gallery initialization failed
      }
    }

    // Here we are using active items offsetWidth to calculate columns width because until the panel is visible on dom the offsetwidth is not availble. So if last selected tab is not company cam then this function is invoked before the container is visible then we don't have access to width, therefore we are using active items width
    const swivuTabPanel = this.up(WIDGET_KEYS.swivuTabPanel);
    /* We have to check for swivuTabPanel because when using with 'EntityManger' there is not tab panel avaialble in that case we just calculate the current element width */
    const singleColumnWidth = ((swivuTabPanel ? swivuTabPanel.activeItem : this).element.offsetWidth - (COLUMN_COUNT * COLUMN_GAP)) / COLUMN_COUNT;
    const projectPhotoGalleryColumns = projectPhotoGallery.items;
    const columns = Array.from({length: COLUMN_COUNT}).map((_item, index) => projectPhotoGalleryColumns[index].totalHeight || 0);

    const images = await Promise.all(photos.map(async photo => {
      let imageURIThumbnail = photo.uris.find((photoUri) => photoUri.type === "thumbnail_annotation");
      return this.getImageAspectsAndBlobUrl(imageURIThumbnail.url, photo, photo.isSwivuImage);
    }))

    images.filter(Boolean).forEach(({ imageBitMap, url: blobImageUrl, photo }, index) => {
      const shortestColumn = columns.reduce(
        (min, height, i) => height < columns[min] ? i : min,
        0
      );
      const possibleImageHeight = (imageBitMap.height / imageBitMap.width) * singleColumnWidth;

      let imageURI = photo.uris.find((photoUri) => photoUri.type === "original_annotation");
      if (!imageURI) imageURI = photo.uris.find((photoUri) => photoUri.type === "original");

      const image = {
        type: "container",
        cls: "cc-image-gallery-item bg-transparent",
        style: `transition: transform 0.3s ease;`,
        items: [
          {
            type: "button",
            contentElementCls: "bg-transparent p-0 border-0 w-full",
            html: StringHelper.xss`<img data-image-url="${imageURI.uri}" data-image-id="${photo.id}" src="${blobImageUrl}" data-index="${index}"  alt="img"/>`,
            onClick: (e) => {
              if (e.event.shiftKey) {
                this.displayInAnnotationEditor(imageURI.uri);
              } else {
                this.displayModal(photo, e.source.parent.parent.parent, imageURI.uri)
              }
            }
          },
          {
            type: "button",
            tooltip: { html: "Permanently Delete", cls: "swivu-modern-tooltip danger", hideDelay: 100, hoverDelay: 100 },
            contentElementCls: "cc-image-gallery-trashcan",
            icon: "fa-solid fa-trash text-danger !text-[2em]",
            onClick: ({ source }) => this.deleteProjectPhoto(photo, source.parent)
          }
        ]
      };
      projectPhotoGallery.items[shortestColumn].add(image);
      projectPhotoGallery.items[shortestColumn].totalHeight += possibleImageHeight;
      columns[shortestColumn] += possibleImageHeight;
    })
  }

  displayInAnnotationEditor(imageURI) {
    const topContainerElement = document.createElement("div");
    topContainerElement.classList.add("swivu-annotation-editor-top-container")
    const containerElement = document.createElement("div");
    containerElement.classList.add("swivu-annotation-editor")
    topContainerElement.appendChild(containerElement)
    getBryntumFloatRootElement().after(topContainerElement);

    const targetImg = document.createElement("img");
    targetImg.src = imageURI;

    const markerArea = new AnnotationEditor();
    markerArea.targetImage = targetImg;

    containerElement.appendChild(markerArea);

    if (markerArea._toolbarContainer) {
      markerArea._toolbarContainer.querySelector("[title='OK']").style.display = "none"
    }

    // handle save event
    /*markerArea.addEventListener("editorsave", () => {
      alert("Saving annotated image not supported....");
      /!*
      const dataUrl = event.detail.dataUrl;
      // download the rasterized image
      if (dataUrl) {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = "annotation.png";
        link.click();
      }*!/
    });*/

    markerArea.addEventListener("editorclose", () => {
      topContainerElement.remove()
    });
  }

  async deleteProjectPhoto(photo, widget) {
    const isDelete = await showConfirmationDialog({
      title: "Confirm delete?",
      message: `Are you sure you want to delete this photo?`,
    })
    if (isDelete) {
      try {
        this.toggleMask(true); // Force show mask
        if (photo.isSwivuImage) {
          this.context.project.boardFilesStore.remove(photo.id)
        } else {
          await deleteCompanyCamPhoto(photo.id, this.companyCamToken.value);
        }
        widget.owner.remove(widget);
        this.swivuTotalPhotoCount--
        if (!this.swivuTotalPhotoCount) this.widgetMap.galleryViewMessageBox.show();
      } catch (e) {
        showToast({ msg: this.parseCompanyCamErrorMessage(e) })
        console.error('Error deleting photo:', e);
      }
      finally {
        this.toggleMask(false); // Force hide mask
      }
    }
  }

  toggleMask(forceState) {
    if (this.companyCamLoader) {
      if (forceState === true) {
        this.companyCamLoader.show();
      } else if (forceState === false) {
        this.companyCamLoader.hide();
      } else {
        // Toggle based on current state if no force parameter
        if (this.companyCamLoader.isVisible) this.companyCamLoader.hide();
        else this.companyCamLoader.show();
      }
    } else {
      this.companyCamLoader = this.mask("Loading...");
    }
  }

  displayModal(photo, widget, imageURI) {
    //Todo: Refactor Required for Image Carousel
    const modal = document.getElementsByClassName('companycam-modal')[0];
    const targetElementId = widget.id
    modal.setAttribute('data-target-element-id', targetElementId);
    const photoId = photo.id;
    // set modal current-photo-id attribute to photo id
    modal.setAttribute('data-current-photo-id', photoId);
    // replace image in modal with clicked image, replacing 400x400 at end of uri with 1200x1200
    document.getElementsByClassName('cc-modal-image')[0].src = imageURI;
    modal.style.display = 'flex';
    this.otherParams?.editor?.revertFocus();
  }

  cleanBlobUrls() {
    if (this.imageBlobUrls) {
      this.imageBlobUrls.forEach((url) => URL.revokeObjectURL(url));
      this.imageBlobUrls = [];
    }
  }

  parseCompanyCamErrorMessage(err) {
    let companyCamErrorMsg = err?.response?.data?.errors;
    if (Array.isArray(companyCamErrorMsg)) {
      companyCamErrorMsg = companyCamErrorMsg.at(0)
    }
    if (companyCamErrorMsg) {
      return `CompanyCam is generating the following error: "${companyCamErrorMsg}"<br/><br/>If the problem persists, login into CompanyCam and review CompanyCam’s activation, system, and downtime alert messages.`
    } else {
      return "An unexpected issue occurred while connecting to CompanyCam.<br/><br/>If the problem persists, please contact Swivu support for assistance."
    }
  }
}

SwivuCompanyCamGalleryView.initClass();
